import React from 'react';
import { Card, Descriptions, Progress, Space, Tag, Typography } from 'antd';
import type {
  ActiveOperationProjection,
  ExecutionProgressProjection,
  RouteStatusProjection
} from '../routes/execution/dashboardProjection';
import type { OperationalActionDescriptor } from '../routes/execution/executionActionsFacade';
import type { ExecutionCargoHydration } from './types';
import OperationalExecutionActions from './OperationalExecutionActions';
import { formatOperationalDateTime } from '../routes/execution/operationalContextFacade';
import type { ExecutionActionSubmitPayload } from './ExecutionActionModal';
import type { IncidentsProjection } from '../routes/execution/dashboardProjection';

const { Text } = Typography;

type Props = {
  routeId: number;
  routeStatus: RouteStatusProjection;
  incidents: IncidentsProjection;
  activeOperation: ActiveOperationProjection | null;
  progress: ExecutionProgressProjection | null;
  actions: OperationalActionDescriptor[];
  cargoAtStop: ExecutionCargoHydration[];
  onSubmit: (payload: ExecutionActionSubmitPayload) => Promise<void>;
  onOpenCompletion?: () => void;
  onModeChange?: () => void;
  actionLoading?: boolean;
};

export const OperationalExecutionHeader: React.FC<Props> = ({
  routeId,
  routeStatus,
  incidents,
  activeOperation,
  progress,
  actions,
  cargoAtStop,
  onSubmit,
  onOpenCompletion,
  onModeChange,
  actionLoading = false
}) => {
  const op = activeOperation;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      <Card size="small" title="Статус маршрута">
        <Space wrap align="center">
          <Tag color={routeStatus.color} style={{ fontSize: 14, padding: '2px 10px' }}>
            {routeStatus.label}
          </Tag>
          {incidents.activeCount > 0 ? (
            <Tag color="red">Инцидент ({incidents.activeCount})</Tag>
          ) : null}
          {routeStatus.hasExecutionFailure ? <Tag color="error">Ошибка исполнения</Tag> : null}
          {progress ? (
            <Text type="secondary">
              {progress.stopsCompleted}/{progress.stopsTotal} точек · {progress.routePercent}%
            </Text>
          ) : null}
        </Space>
        {progress ? <Progress percent={progress.routePercent} style={{ marginTop: 12 }} /> : null}
      </Card>

      {op ? (
        <Card size="small" title="Текущая операция">
          <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
            <Descriptions.Item label="Тип">
              {op.operationType === 'loading' ? 'Погрузка' : 'Выгрузка'}
            </Descriptions.Item>
            <Descriptions.Item label="Статус">
              <Tag color={op.statusKey.includes('failed') ? 'error' : 'processing'}>{op.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Адрес" span={2}>
              {op.address ?? '—'}
            </Descriptions.Item>
            {op.application.number ? (
              <Descriptions.Item label="Заявка">№{op.application.number}</Descriptions.Item>
            ) : null}
            {op.application.client ? (
              <Descriptions.Item label="Клиент">{op.application.client}</Descriptions.Item>
            ) : null}
            {op.application.consignee ? (
              <Descriptions.Item label="Грузополучатель" span={2}>
                {op.application.consignee}
              </Descriptions.Item>
            ) : null}
            <Descriptions.Item label="План">{formatOperationalDateTime(op.plannedAt)}</Descriptions.Item>
            <Descriptions.Item label="Факт">{formatOperationalDateTime(op.actualAt)}</Descriptions.Item>
            <Descriptions.Item label="Прогресс" span={2}>
              <Progress percent={op.progressPercent} size="small" />
            </Descriptions.Item>
          </Descriptions>
          {op.cargoSummary.length > 0 ? (
            <div style={{ marginTop: 12 }}>
              <Text strong style={{ fontSize: 12 }}>
                Грузы:
              </Text>
              {op.cargoSummary.map((c) => (
                <Text key={c.cargoName} style={{ display: 'block', fontSize: 12 }}>
                  · {c.cargoName} — {c.qty} шт
                </Text>
              ))}
            </div>
          ) : null}
          {op.remainingSummary.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <Text strong style={{ fontSize: 12 }}>
                Осталось:
              </Text>
              {op.remainingSummary.map((c) => (
                <Text key={`r-${c.cargoName}`} style={{ display: 'block', fontSize: 12 }}>
                  · {c.cargoName} — {c.qty} шт
                </Text>
              ))}
            </div>
          ) : null}
        </Card>
      ) : (
        <Card size="small">
          <Text type="secondary">Нет активной операции — все точки завершены или ожидают начала.</Text>
        </Card>
      )}

      <Card size="small" title="Действия">
        <OperationalExecutionActions
          routeId={routeId}
          actions={actions}
          cargoAtStop={cargoAtStop}
          onSubmit={onSubmit}
          onOpenCompletion={onOpenCompletion}
          onModeChange={onModeChange}
          loading={actionLoading}
        />
      </Card>
    </Space>
  );
};

export default OperationalExecutionHeader;
